import React from 'react';
import { Panel, PanelGroup } from 'react-bootstrap';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import get from 'lodash/get';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  RadioField,
  ColorField,
  SliderField,
  FourField,
  CodeField,
  InputField,
  SelectField,
  DateRadioField,
  CheckboxField,
  required,
  number,
  minValue,
  maxValue,
  isValidSlug,
} from '../../form';

function submitTemplateForm(values, _dispatch, component) {
  component.handleTemplateSubmit(values);
}

const TemplatesForm = ({ formAction, isAdmin, handleSubmit, isDefault }) => {
  return (
    <form onSubmit={handleSubmit}>
      <PanelGroup accordion id="accordion-template-settings" defaultActiveKey='1'>
        <Panel eventKey="1">
          <Panel.Heading>
            <Panel.Title toggle>General Setting</Panel.Title>
          </Panel.Heading>
          <Panel.Collapse>
            <Panel.Body>
              <Field
                name="template[name]"
                label="Name"
                component={InputField}
                type="text"
                props={{
                  disabled: formAction === 'Update'
                }}
                placeholder="Name of your template"
                validate={[required, isValidSlug]}
              />
              {
                isAdmin && (
                  <Field
                    type='checkbox'
                    name='template[default]'
                    label='Set As Default'
                    disabled={isDefault}
                    singleLine
                    component={CheckboxField}
                  />
                )
              }
              <Field
                name='template[device_type]'
                label="Device Type"
                options={[['desktop', 'Desktop'], ['mobile', 'Mobile']]}
                component={RadioField}
                defaultValue='desktop'
              />
              <Field
                name='template[desc_length]'
                label='Description Length'
                minValue={10}
                maxValue={300}
                unit="chr"
                component={SliderField}
              />
              <Field
                name='template[date_format]'
                label="Date Format"
                options={[
                  ['relative', 'Relative'],
                  ['long', 'MMM, DD, YYYY'],
                  ['iso8601', 'YYYY/MM/DD'],
                  ['MM/DD/YYYY', 'MM/DD/YYYY'],
                  ['MM/DD/YY', 'MM/DD/YY'],
                  ['DD/MM/YYYY', 'DD/MM/YYYY']
                ]}
                defaultValue={'relative'}
                component={DateRadioField}
              />
              <Field
                type='text'
                name='template[slots]'
                label='Slots'
                component={SelectField}
                options={[
                  { label: '1', value: 1 },
                  { label: '2', value: 2 },
                  { label: '3', value: 3 },
                  { label: '4', value: 4 },
                  { label: '5', value: 5 },
                  { label: '6', value: 6 },
                  { label: '7', value: 7 },
                  { label: '8', value: 8 },
                  { label: '9', value: 9 },
                  { label: '10', value: 10 },
                  { label: '11', value: 11 },
                  { label: '12', value: 12 },
                  { label: '13', value: 13 },
                  { label: '14', value: 14 },
                  { label: '15', value: 15 }
                ]}
                normalize={(e) => e.value}
                validate={[required, number, minValue(1), maxValue(15)]}
                unclearable
              />
            </Panel.Body>
          </Panel.Collapse>
        </Panel>

        <Panel eventKey="2">
          <Panel.Heading>
            <Panel.Title toggle>Label Settings</Panel.Title>
          </Panel.Heading>
          <Panel.Collapse>
            <Panel.Body>
              <Field
                name='template[custom_label]'
                label="Label Type"
                options={[['true', 'Custom'], ['false', 'Standard']]}
                component={RadioField}
              />

              <Field
                name='template[label][styles][fontSize]'
                label='Font Size'
                minValue={0}
                maxValue={100}
                unit="px"
                component={SliderField}
              />

              <Field
                name='template[label][styles][textAlign]'
                label='Text Align'
                options={[['left', 'Left'], ['center', 'Center'], ['right', 'Right']]}
                component={RadioField}
              />

              <Field
                name='template[label][styles][color]'
                label='Font Color'
                component={ColorField}
              />

              <Field
                name='template[label][styles][backgroundColor]'
                label='Background Color'
                component={ColorField}
              />

              <Field
                name='template[label][styles][margin]'
                label='Margin'
                component={FourField}
              />

              <Field
                name='template[label][styles][padding]'
                label='Padding'
                component={FourField}
              />
            </Panel.Body>
          </Panel.Collapse>
        </Panel>

        <Panel eventKey="3">
          <Panel.Heading>
            <Panel.Title toggle>Custom HTML</Panel.Title>
          </Panel.Heading>
          <Panel.Collapse>
            <Panel.Body>
              <Field
                name='template[snippet]'
                label='Custom HTML'
                component={CodeField}
                disabled={!isAdmin}
                idName="html"
                insertItems={[
                  {
                    displayText: 'Job Title',
                    text: '<%=title%>'
                  },
                  {
                    displayText: 'Company',
                    text: '<%=company%>'
                  },
                  {
                    displayText: 'Job Description',
                    text: '<%=description%>'
                  },
                  {
                    displayText: 'Location',
                    text: '<%=location%>'
                  },
                  {
                    displayText: 'Date',
                    text: '<%=date%>'
                  },
                  {
                    displayText: 'Job URL',
                    text: '<%=url%>'
                  },
                  {
                    displayText: 'Click Parameters',
                    text: '<%=clickparam%>'
                  }
                ]}
              />
            </Panel.Body>
          </Panel.Collapse>
        </Panel>

        <Panel eventKey="4">
          <Panel.Heading>
            <Panel.Title toggle>Custom CSS</Panel.Title>
          </Panel.Heading>
          <Panel.Body collapsible>
            <Field
              name='template[custom_css]'
              label='Custom CSS'
              component={CodeField}
              insertItems={[]}
              disabled={!isAdmin}
              idName="css"
              mode="css"
            />
            <span>
              <FontAwesomeIcon icon="info-circle" />&nbsp;
              <strong>Using existing named style classes and ids may override the websites existing styles</strong>
            </span>
        </Panel.Body>
        </Panel>
        <Panel eventKey="5">
          <Panel.Heading>
            <Panel.Title toggle>Custom JS</Panel.Title>
          </Panel.Heading>
          <Panel.Body collapsible>
            <Field
              name='template[custom_js]'
              label='Custom JS'
              component={CodeField}
              disabled={!isAdmin}
              insertItems={[]}
              idName="js"
              mode="javascript"
            />
            <span>
              <FontAwesomeIcon icon="info-circle" />&nbsp;
              <strong>Rendering Javascript may have undesired effects.  Use caution when writing custom javascript</strong>
            </span>
          </Panel.Body>
        </Panel>
      </PanelGroup>
    </form>
  );
};

const ResourceForm = reduxForm({
  form: 'TemplateForm',
  onSubmit: submitTemplateForm,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});

const mapStateToProps = (_state, ownProps) => {
  const { website, template, isAdmin } = ownProps;

  const initialValues = {
    template: {
      name: template.name,
      default: template.default,
      uuid: template.uuid,
      slots: template.slots || website.default_slots,
      device_type: template.device_type,
      custom_label: String(template.custom_label),
      date_format: template.date_format,
      desc_length: template.desc_length || 60,
      label: {
        styles: {
          textAlign: get(template, 'label.styles.textAlign', 'left'),
          fontSize: parseInt(get(template, 'label.styles.fontSize', 18), 10),
          color: {
            r: parseInt(get(template, 'label.styles.color.r', 0), 10),
            g: parseInt(get(template, 'label.styles.color.g', 0), 10),
            b: parseInt(get(template, 'label.styles.color.b', 0), 10),
            a: parseFloat(get(template, 'label.styles.color.a', 1.0))
          },
          backgroundColor: {
            r: parseInt(get(template, 'label.styles.backgroundColor.r', 255), 10),
            g: parseInt(get(template, 'label.styles.backgroundColor.g', 255), 10),
            b: parseInt(get(template, 'label.styles.backgroundColor.b', 255), 10),
            a: parseFloat(get(template, 'label.styles.backgroundColor.a', 1.0))
          },
          margin: {
            top: parseInt(get(template, 'label.styles.margin.top', 0), 10),
            right: parseInt(get(template, 'label.styles.margin.right', 0), 10),
            bottom: parseInt(get(template, 'label.styles.margin.bottom', 0), 10),
            left: parseInt(get(template, 'label.styles.margin.left', 0), 10)
          },
          padding: {
            top: parseInt(get(template, 'label.styles.padding.top', 0), 10),
            right: parseInt(get(template, 'label.styles.padding.right', 0), 10),
            bottom: parseInt(get(template, 'label.styles.padding.bottom', 0), 10),
            left: parseInt(get(template, 'label.styles.padding.left', 0), 10)
          }
        },
        snippet: get(template, 'label.snippet', ''),
      },
      snippet: template.snippet,
      custom_css: template.custom_css,
      custom_js: template.custom_js
    }
  };
  const isDefault = template.default;

  return { initialValues, isAdmin, template, isDefault };
};

export default connect(mapStateToProps)(ResourceForm(TemplatesForm));
